import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, SquareParking, Waves, Flower2, Dumbbell, ConciergeBell, Umbrella } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n";

const pub = (f) => `${process.env.PUBLIC_URL}/${f}`;

// Amenity icons, mapped by index to the `amenities` array
// (Private Parking, Outdoor Pool, Spa, Gym, 24/7 Concierge, Private Beach).
const AMENITY_ICONS = [SquareParking, Waves, Flower2, Dumbbell, ConciergeBell, Umbrella];

// Meliora Group — the residential sub-group. Brown palette taken straight from the
// homepage Sectors tile (#6f5744 → #5a4434 → #47352e), white text + warm-gold accents.
export default function ResidentialPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const r = t.residential;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const backHome = () => {
    navigate("/");
    setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  return (
    <main className="bg-[#5a4434] text-white">
      {/* ───────────── OPENING — Meliora logo + tagline over fine "blueprint" lines (Taurus-style) ───────────── */}
      <section className="relative flex flex-col items-center overflow-hidden bg-gradient-to-br from-[#6f5744] via-[#5a4434] to-[#47352e] px-6 pb-16 pt-28 text-center sm:pb-24 sm:pt-36">
        {/* fine vertical white hairlines */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 9%)",
          }}
        />
        {/* soft light from the top */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(110%_70%_at_50%_0%,rgba(255,255,255,0.08),transparent_60%)]"
        />

        <div className="relative flex flex-col items-center">
          <Reveal delay={0.12}>
            <img
              src={pub("MelioraLogoWhite.svg")}
              alt="Meliora"
              className="w-[min(84vw,400px)] drop-shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
            />
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mx-auto mt-3 h-px w-14 bg-white/20 sm:mt-5" />
            <p className="mx-auto mt-3 max-w-xl font-serif text-xl font-light leading-relaxed text-white/80 sm:mt-5 sm:text-2xl">
              {r.heroTagline} {r.heroLine2}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────── MELIORA TOWER MAMAIA — render + story + amenities ───────────── */}
      <section className="bg-[#47352e] py-20 md:py-28">
        <div className="container-luxe grid items-center gap-10 md:grid-cols-2 md:gap-16">
          <Reveal>
            <div className="relative mx-auto w-full max-w-[440px] overflow-hidden rounded-2xl border border-white/10 shadow-[0_34px_90px_-44px_rgba(0,0,0,0.7)]">
              <img src={pub("melioraHeroSection.jpeg")} alt={r.towerBrand} className="block w-full" />
              {/* slight darkening + upcoming badge */}
              <div className="pointer-events-none absolute inset-0 bg-black/35" />
              <span className="absolute right-4 top-4 rounded-full border border-[#e7cf9a]/50 bg-black/40 px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.28em] text-[#e7cf9a] backdrop-blur-sm">
                {t.sectors.soon}
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl font-light text-white sm:text-4xl md:text-[2.75rem]">
              {r.towerBrand}{" "}
              <span className="whitespace-nowrap text-xl text-[#e7cf9a]/70 sm:text-2xl">({t.sectors.soon})</span>
            </h2>
            <div className="mt-4 h-px w-12 bg-[#e7cf9a]/50" />
            <p className="mt-5 font-serif text-lg italic text-[#e7cf9a] md:text-xl">{r.towerLead}</p>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-white/70 md:text-base">
              {r.towerBody.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Amenities */}
        <div className="container-luxe mt-16 md:mt-24">
          <Reveal>
            <p className="text-center text-[11px] font-medium uppercase tracking-[0.36em] text-[#e7cf9a]">
              {r.amenitiesTitle}
            </p>
            <div className="mx-auto mt-9 grid max-w-4xl grid-cols-2 gap-x-6 gap-y-9 sm:grid-cols-3 lg:grid-cols-6">
              {r.amenities.map((a, i) => {
                const Icon = AMENITY_ICONS[i] || Waves;
                return (
                  <div key={a} className="flex flex-col items-center gap-3 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#e7cf9a]/30">
                      <Icon size={24} strokeWidth={1.5} className="text-[#e7cf9a]" />
                    </span>
                    <span className="text-[11px] uppercase leading-snug tracking-[0.14em] text-white/80">{a}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── MELIORA TABACARIE LAKE — first residential landmark + story + figures ───────────── */}
      <section className="bg-[#5a4434] py-20 md:py-28">
        <div className="container-luxe grid items-center gap-10 md:grid-cols-2 md:gap-14">
          <Reveal>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {["MelioraTAB1.png", "MelioraTAB2.png"].map((src) => (
                <div
                  key={src}
                  className="group overflow-hidden rounded-2xl border border-white/10 shadow-[0_34px_90px_-44px_rgba(0,0,0,0.7)]"
                >
                  <img
                    src={pub(src)}
                    alt={r.tabacarieiBrand}
                    loading="lazy"
                    className="block h-full w-full object-cover transition-transform [transition-duration:1400ms] ease-out group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl font-light text-white sm:text-4xl md:text-[2.75rem]">
              {r.tabacarieiBrand}
            </h2>
            <div className="mt-4 h-px w-12 bg-[#e7cf9a]/50" />
            <p className="mt-5 font-serif text-lg italic text-[#e7cf9a] md:text-xl">{r.tabacarieiLead}</p>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-white/70 md:text-base">
              {r.tabacarieiBody.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Key figures */}
        <div className="container-luxe mt-14 md:mt-20">
          <Reveal>
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-8 border-y border-white/15 py-9 md:gap-x-16">
              {r.tabacarieiStats.map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-serif text-3xl font-light text-white md:text-4xl">{s.value}</div>
                  <div className="mt-2.5 text-[10px] uppercase tracking-[0.22em] text-[#e7cf9a]/70">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── MELIORA POPORULUI — photo + story + figures ───────────── */}
      <section className="bg-[#47352e] py-20 md:py-28">
        <div className="container-luxe grid items-center gap-10 md:grid-cols-2 md:gap-14">
          <Reveal>
            <div className="group overflow-hidden rounded-2xl border border-white/10 shadow-[0_34px_90px_-44px_rgba(0,0,0,0.7)]">
              <img
                src={pub("melioraPoporului.jpeg")}
                alt={r.poporuluiBrand}
                loading="lazy"
                className="block w-full transition-transform [transition-duration:1400ms] ease-out group-hover:scale-105"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl font-light text-white sm:text-4xl md:text-[2.75rem]">
              {r.poporuluiBrand}
            </h2>
            <div className="mt-4 h-px w-12 bg-[#e7cf9a]/50" />
            <p className="mt-5 font-serif text-lg italic text-[#e7cf9a] md:text-xl">{r.poporuluiLead}</p>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-white/70 md:text-base">
              {r.poporuluiBody.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Key figures */}
        <div className="container-luxe mt-14 md:mt-20">
          <Reveal>
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-8 border-y border-white/15 py-9 md:gap-x-16">
              {r.stats.map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-serif text-3xl font-light text-white md:text-4xl">{s.value}</div>
                  <div className="mt-2.5 text-[10px] uppercase tracking-[0.22em] text-[#e7cf9a]/70">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── Closing — back to the Sectors grid ───────────── */}
      <section className="bg-[#3a2a22] py-20 md:py-28">
        <Reveal>
          <div className="container-luxe text-center">
            <span className="mx-auto mb-8 block h-px w-12 bg-[#e7cf9a]/50" />
            <button
              onClick={backHome}
              className="inline-flex items-center gap-2 border border-[#e7cf9a]/50 px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#e7cf9a] transition-colors duration-300 hover:border-[#e7cf9a] hover:bg-[#e7cf9a] hover:text-[#47352e]"
            >
              <ArrowLeft size={16} /> {r.back}
            </button>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
